import { Component, inject, input, OnInit, signal } from '@angular/core';
import { Incident } from '../../models/incident';
import { Globals } from '../functions';
import { TyreLocation, WaGroupFlag } from '../enums';
import { AppModule } from '../../app.module';
import { NotificationService } from '../notification.service';
import { IncidentService } from '../incident.service';
import { environment } from '../../../environments/environment';

// ─── Data structures ────────────────────────────────────────────────────────

export interface WaMessageButton {
  /** Label shown on the button, e.g. "30 min" */
  label: string;
  /** Text appended to the base message text when the button is clicked */
  appendText: string;
}

export interface WaMessage {
  /** The assembled message text – updated when an append button is clicked */
  text: string;
  /** The interpolated template text before any button appends */
  baseText: string;
  /** Bitwise combination of WaGroupFlag values that must ALL be set for this message to show */
  requiredFlags?: number;
  /** Optional append buttons shown to the right of the message */
  buttons: WaMessageButton[];
}

/** Internal template definition – resolved into WaMessage at display time */
interface WaMessageTemplate {
  templateFn: (incident: Incident, etaMinutes: number | null) => string;
  /** ALL of these bits must be set (AND). Omit to skip. */
  requiredFlags?: number;
  /** At least ONE of these bits must be set (OR). Omit to skip. */
  anyFlags?: number;
  buttons: WaMessageButton[];
}

// ─── Hardcoded message templates ────────────────────────────────────────────

const MESSAGE_TEMPLATES: WaMessageTemplate[] = [
  {
    // Shown when there is a direct BD Tech 
    templateFn: (_) => 'Reminder: Please accept the breakdown via the App.',
    requiredFlags: WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  {
    // Shown when there is a direct BD Tech 
    templateFn: (_) => 'Reminder: Please adjust the ETA via the App.',
    requiredFlags: WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  {
    // Shown when there is a direct BD Tech 
    templateFn: (_) => 'Reminder: Please add images via the App.',
    requiredFlags: WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  {
    // Shown for 3rd party OR SP-controls-BD-tech OR Direct-to-BD-tech 
    templateFn: (incident, etaMinutes) => {
      const appLink = `${environment.appUrl}action/${incident.incidentId}`;
      const tyre = incident.tyrePositions?.[0]?.sizeDescription ?? '';
      const position = (TyreLocation[incident.tyrePositions?.[0]?.tyreLocation] ?? '').toUpperCase();
      const brandMap: Record<number, string> = { [-1]: 'IMPORT', [-2]: 'RETREAD', [-3]: 'ANY', [-4]: 'LOCAL', [-5]: 'SPARE', [-6]: 'OWN STOCK' };
      const type = brandMap[incident.tyreBrands?.[0]] ?? 'UNKNOWN';
      const controllerOrTransporter = incident.clientName
        ? `*FLEET CONTROLLER:* ${incident.clientName}\n`
        : incident.transporterName
          ? `*TRANSPORTER:* ${incident.transporterName}\n`
          : '';
      const incidentNum = incident.orderNumber ? `*NUMBER:* ${incident.orderNumber}\n` : '';

      return `🚛 New Breakdown Available 🚛\n\n${controllerOrTransporter}${incidentNum}*TYRE:* ${tyre}\n*POSITION:* ${position}\n*TYPE:* ${type}\n\nUse the link to accept and update the progress of the breakdown.\n\n${appLink}`;
    },
    anyFlags: WaGroupFlag.ThirdPartySP | WaGroupFlag.SPControlsBDTech | WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  {
    // Shown for 3rd party OR SP-controls-BD-tech OR Direct-to-BD-tech 
    templateFn: (incident, etaMinutes) => {      
      return `Follow the link for training on how to capture the breakdown via our new system🤓\n
      https://us06web.zoom.us/clips/share/kW6ssMUvTemp2XkPnqXRVw`;
    },
    anyFlags: WaGroupFlag.ThirdPartySP | WaGroupFlag.SPControlsBDTech | WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },

  // {
  //   // Shown for 3rd party OR SP-controls-BD-tech
  //   templateFn: (incident) => {
  //     const reg = incident.fleetVehicle?.registrationNumber ?? '';
  //     const tyres = incident.tyrePositions?.map(t => t.sizeDescription).filter(Boolean).join(', ') ?? '';
  //     const desc = incident.incidentDescription ?? '';
  //     return `Vehicle Reg: ${reg}\nTyre: ${tyres}\nDescription: ${desc}`;
  //   },
  //   anyFlags: WaGroupFlag.ThirdPartySP | WaGroupFlag.SPControlsBDTech,
  //   buttons: [{ label: 'Copy',  appendText: '' }],
  // },
  {
    // Shown for 3rd party – full breakdown details
    templateFn: (incident) => {
      const reg = incident.fleetVehicle?.registrationNumber ?? '';
      const transporter = incident.transporterName || incident.clientName || '';
      const tyre = incident.tyrePositions?.[0]?.sizeDescription ?? '';
      const position = (TyreLocation[incident.tyrePositions?.[0]?.tyreLocation] ?? '').toUpperCase();
      const brandMap: Record<number, string> = { [-1]: 'IMPORT', [-2]: 'RETREAD', [-3]: 'ANY', [-4]: 'LOCAL', [-5]: 'SPARE', [-6]: 'OWN STOCK' };
      const type = brandMap[incident.tyreBrands?.[0]] ?? 'UNKNOWN';
      const driverName = incident.driver?.name ?? '';
      const driverCell = incident.driver?.phoneNumber ?? '';
      const desc = incident.incidentDescription ?? '';
      return [
        '*BREAKDOWN DETAILS* 🚚',
        ...(incident.orderNumber ? [`*NUMBER:* ${incident.orderNumber}`] : []),
        `*VEHICLE REG:* ${reg}`,
        `*TRANSPORTER:* ${transporter}`,
        `*TYRE:* ${tyre}`,
        `*POSITION:* ${position}`,
        `*TYPE:* ${type}`,
        `*DRIVER:* ${driverName}`,
        `*DRIVER CELL:* ${driverCell}`,
        '-----------------------------------------------------',
        'PLEASE DONT FORGET TO:',
        '- Use the link to action the breakdown.',
        '- Let us know when you have dispatched',
        '- Take pictures of vehicle registration as soon as you are on site.',
        '- Take all required pictures.',
        '- Promptly lets us know of any delays.',
        '-----------------------------------------------------',
        'EXTRA BREAKDOWN INFO:',
        desc,
      ].join('\n');
    },
    anyFlags: WaGroupFlag.ThirdPartySP | WaGroupFlag.SPControlsBDTech | WaGroupFlag.DirectToBDTech,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  
  {
    // Shown for fleet – ETA from override or incident
    templateFn: (_incident, etaMinutes) => {
      const eta = etaMinutes !== null && etaMinutes > 0 ? `${etaMinutes}` : '?';
      return `The ETA is ${eta} minutes.`;
    },
    requiredFlags: WaGroupFlag.Fleet,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  
  {
    // Shown for fleets
    templateFn: (_) => 'The ETA is: ',
    requiredFlags: WaGroupFlag.Fleet,
    buttons: [
      { label: '15 min',  appendText: '15 minutes.' },
      { label: '30 min',  appendText: '30 minutes.' },
      { label: '45 min',  appendText: '45 minutes.' },
      { label: '1 hour',  appendText: '1 hour' },
      { label: '1.5 hours',  appendText: '1.5 hours' },      
      { label: '2 hours', appendText: '2 hours' },
    ],
  },
  {
    // Shown for fleet
    templateFn: (_) => 'The Breakdown Technician is on site.',
    requiredFlags: WaGroupFlag.Fleet,
    buttons: [{ label: 'Copy',  appendText: '' }],
  },
  {
    // Shown for 3rd party OR SP-controls-BD-tech OR Direct-to-BD-tech 
    templateFn: (incident, etaMinutes) => {
      const appLink = `${environment.appUrl}report-new-breakdown/${incident.incidentId}`;
      const reg = incident.fleetVehicle?.registrationNumber || 'Breakdown';
      return `🛻 Tracking link for: ${reg} 🛻\n\nUse for live updates on ETA, delays, onsite arrival, images and completion status.\n\n${appLink}`;
    },
    anyFlags: WaGroupFlag.Fleet,
    buttons: [{ label: 'Copy',  appendText: '' }],
  }
];


@Component({
  selector: 'app-wa-group-opener',
  imports: [AppModule],
  templateUrl: './wa-group-opener.html',
  styleUrl: './wa-group-opener.less'
})
export class WaGroupOpener implements OnInit {
  incident = input.required<Incident>();
  whatsappLink = input.required<string>();
  /** When true the Fleet flag is set so fleet-specific message templates are shown. */
  isFleet = input<boolean>(false);
  /** Optional ETA in minutes. If provided, overrides the ETA calculated from breakdownTechnicianDriverEta. */
  etaOverride = input<number | null>(null);
  /** When set, overrides the default active-SP lookup and uses this specific service provider instead. */
  serviceProviderId = input<number | null>(null);

  flags   = signal<number>(WaGroupFlag.Unknown);
  messages = signal<WaMessage[]>([]);

  constructor(private notification: NotificationService, private incidentService: IncidentService) {}

  ngOnInit(): void {
    this.onDisplay();
  }

  onDisplay(): void {
    const incident = this.incident();
    let flags = WaGroupFlag.Unknown;

    if (this.isFleet()) {
      flags |= WaGroupFlag.Fleet;
    }

    // Determine flags from the service provider (specific override or active)
    const spId = this.serviceProviderId();
    const activeSP = spId
      ? incident.incidentClients.find(cl => cl.clientId === spId && cl.connectionTypeId === 3)
      : incident.incidentClients.find(cl => cl.isActive && cl.connectionTypeId === 3);
    if (!this.isFleet()) {
      console.log("Active SP", activeSP);
      if (activeSP) {
        if (activeSP.canSelectBreakdownTechnician === true) {
          flags |= WaGroupFlag.DirectToBDTech;
        } else if (activeSP.canSelectBreakdownTechnician === false) {
          flags |= WaGroupFlag.SPControlsBDTech;
        } else if (activeSP.canSelectBreakdownTechnician === null) {
          flags |= WaGroupFlag.ThirdPartySP;
        }
      } else {
        // TODO: check for call centre
        flags |= WaGroupFlag.CallCentre;
      }
    }

    // If a BD technician is assigned, set that flag too
    if (incident.breakdownTechnician?.driverId) {
      flags |= WaGroupFlag.HasBDTechAssigned;
    }

    this.flags.set(flags);
    console.log("Flags", this.flags());
    

    // Resolve ETA: prefer override input (already in minutes), else derive from incident
    const etaMinutes: number | null = this.etaOverride() ?? (
      incident.breakdownTechnicianDriverEta
        ? Globals.Functions.calculateMinutes(incident.breakdownTechnicianDriverEta)
        : null
    );

    // Resolve visible message templates
    const resolved: WaMessage[] = MESSAGE_TEMPLATES
      .filter(tpl => {
        const allMatch = !tpl.requiredFlags || (flags & tpl.requiredFlags) === tpl.requiredFlags;
        const anyMatch = !tpl.anyFlags || (flags & tpl.anyFlags) !== 0;
        return allMatch && anyMatch;
      })
      .map(tpl => {
        const baseText = tpl.templateFn(incident, etaMinutes);
        return { baseText, text: baseText, requiredFlags: tpl.requiredFlags, buttons: tpl.buttons };
      });

    this.messages.set(resolved);
  }

  onAppendAndOpen(msg: WaMessage, button: WaMessageButton): void {
    const text = msg.baseText + button.appendText;
    this.copyToClipboard(text, true);
  }

  onCopyOnly(msgIndex: number): void {
    this.copyToClipboard(this.messages()[msgIndex].text, false);
  }

  openWhatsApp(): void {
    const link = this.whatsappLink();
    const windowName = 'wa_' + link.replace(/[^a-zA-Z0-9]/g, '_');
    console.log("Opening window", windowName);
    
    window.open(link, windowName);
  }

  private copyToClipboard(text: string, openLink: boolean): void {
    //Fire (and forget) write to log
    this.incidentService.writeIncidentLogBackground(this.incident().incidentId, this.isFleet() ? 306 : 305, text);
    navigator.clipboard.writeText(text).then(() => {
      if (openLink) {
        this.notification.showInfo('Copied! Opening WhatsApp...');
        this.openWhatsApp();
      } else {
        this.notification.showInfo('Copied to clipboard.');
      }
    }).catch(() => {
      this.notification.showError('Could not copy to clipboard.');
    });
  }
}
